import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Users, Music, AlertTriangle, CheckCircle, Star, ExternalLink, Calendar, Map, Edit, ShieldCheck, MoreHorizontal, Mail, Phone, TrendingUp, DollarSign, FileText, Database, Link as LinkIcon, RefreshCw } from 'lucide-react';
import { useParams, Link } from 'react-router-dom';
const TABS = ['Overview', 'Contacts', 'Fit', 'History', 'Source Data'];
export function VenueDetailPage() {
  const {
    venueId
  } = useParams();
  const [activeTab, setActiveTab] = useState('Overview');
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="space-y-6" data-id="element-4842">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-4843">
        <div data-id="element-4844">
          <div className="flex items-center space-x-3 mb-2" data-id="element-4845">
            <h1 className="text-3xl font-bold text-zinc-100" data-id="element-4846">
              The Empty Bottle
            </h1>
            <span className="px-2.5 py-1 bg-green-500/10 text-green-500 text-xs font-medium rounded-full border border-green-500/20 flex items-center" data-id="element-4847">
              <ShieldCheck className="w-3 h-3 mr-1" data-id="element-4848" />
              Claimed
            </span>
            <span className="px-2.5 py-1 bg-amber-500/10 text-amber-500 text-xs font-medium rounded-full border border-amber-500/20 flex items-center" data-id="element-4849">
              <Star className="w-3 h-3 mr-1" data-id="element-4850" />
              92% Confidence
            </span>
          </div>
          <div className="flex items-center space-x-4 text-zinc-400 text-sm" data-id="element-4851">
            <span className="flex items-center" data-id="element-4852">
              <MapPin className="w-4 h-4 mr-1" data-id="element-4853" /> Chicago, IL
            </span>
            <span className="flex items-center" data-id="element-4854">
              <Users className="w-4 h-4 mr-1" data-id="element-4855" /> 400 Cap (Main Room)
            </span>
            <span className="flex items-center" data-id="element-4856">
              <Music className="w-4 h-4 mr-1" data-id="element-4857" /> Indie, Punk, Electronic
            </span>
          </div>
        </div>
        <div className="flex items-center space-x-3" data-id="element-4858">
          <button className="px-4 py-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-100 text-sm font-medium rounded-lg transition-colors flex items-center" data-id="element-4859">
            <Map className="w-4 h-4 mr-2" data-id="element-4860" />
            Open Map
          </button>
          <button className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-zinc-900 text-sm font-medium rounded-lg transition-colors" data-id="element-4861">
            Add to Targets
          </button>
          <button className="p-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-400 rounded-lg transition-colors" data-id="element-4862">
            <MoreHorizontal className="w-4 h-4" data-id="element-4863" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 border-b border-zinc-800 overflow-x-auto no-scrollbar" data-id="element-4864">
        {TABS.map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab ? 'border-amber-500 text-amber-500' : 'border-transparent text-zinc-400 hover:text-zinc-100 hover:border-zinc-700'}`} data-id="element-4865">
            {tab}
          </button>)}
      </div>

      {/* Tab Content */}
      {activeTab === 'Overview' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4866">
          <div className="lg:col-span-2 space-y-6" data-id="element-4867">
            <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6" data-id="element-4868">
              <h3 className="text-lg font-medium text-zinc-100 mb-4" data-id="element-4869">
                Venue Summary
              </h3>
              <div className="grid grid-cols-2 gap-6" data-id="element-4870">
                <div data-id="element-4871">
                  <p className="text-sm text-zinc-500 mb-1" data-id="element-4872">Address</p>
                  <p className="text-zinc-300" data-id="element-4873">
                    1035 N Western Ave
                    <br data-id="element-4874" />
                    Chicago, IL 60622
                  </p>
                </div>
                <div data-id="element-4875">
                  <p className="text-sm text-zinc-500 mb-1" data-id="element-4876">Website</p>
                  <a href="#" className="text-amber-500 hover:underline flex items-center" data-id="element-4877">
                    emptybottle.com <ExternalLink className="w-3 h-3 ml-1" data-id="element-4878" />
                  </a>
                </div>
                <div data-id="element-4879">
                  <p className="text-sm text-zinc-500 mb-1" data-id="element-4880">Primary Genres</p>
                  <p className="text-zinc-300" data-id="element-4881">
                    Indie Rock, Punk, Electronic, Experimental
                  </p>
                </div>
                <div data-id="element-4882">
                  <p className="text-sm text-zinc-500 mb-1" data-id="element-4883">Age Policy</p>
                  <p className="text-zinc-300" data-id="element-4884">21+ Only</p>
                </div>
              </div>
            </div>

            <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6" data-id="element-4885">
              <h3 className="text-lg font-medium text-zinc-100 mb-4" data-id="element-4886">
                Rooms & Capacity
              </h3>
              <div className="space-y-4" data-id="element-4887">
                <div className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg border border-zinc-800/50" data-id="element-4888">
                  <div data-id="element-4889">
                    <h4 className="font-medium text-zinc-100" data-id="element-4890">Main Room</h4>
                    <p className="text-sm text-zinc-400" data-id="element-4891">Standing Room Only</p>
                  </div>
                  <div className="text-right" data-id="element-4892">
                    <div className="text-lg font-medium text-zinc-100" data-id="element-4893">400</div>
                    <p className="text-xs text-zinc-500" data-id="element-4894">Capacity</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6" data-id="element-4895">
            <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6" data-id="element-4896">
              <h3 className="text-lg font-medium text-zinc-100 mb-4" data-id="element-4897">
                Booking Snapshot
              </h3>
              <div className="space-y-4" data-id="element-4898">
                <div className="flex justify-between items-center" data-id="element-4899">
                  <span className="text-zinc-400" data-id="element-4900">Avg. Ticket Price</span>
                  <span className="text-zinc-100" data-id="element-4901">$15 - $25</span>
                </div>
                <div className="flex justify-between items-center" data-id="element-4902">
                  <span className="text-zinc-400" data-id="element-4903">Typical Deal</span>
                  <span className="text-zinc-100" data-id="element-4904">Door Deal / Vs</span>
                </div>
                <div className="flex justify-between items-center" data-id="element-4905">
                  <span className="text-zinc-400" data-id="element-4906">Curfew</span>
                  <span className="text-zinc-100" data-id="element-4907">2:00 AM</span>
                </div>
              </div>
            </div>

            <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6" data-id="element-4908">
              <h3 className="text-lg font-medium text-zinc-100 mb-4" data-id="element-4909">
                Contact Quality
              </h3>
              <div className="flex items-center space-x-4 mb-4" data-id="element-4910">
                <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center" data-id="element-4911">
                  <CheckCircle className="w-6 h-6 text-green-500" data-id="element-4912" />
                </div>
                <div data-id="element-4913">
                  <div className="text-xl font-bold text-zinc-100" data-id="element-4914">
                    Excellent
                  </div>
                  <p className="text-sm text-zinc-400" data-id="element-4915">Verified 2 days ago</p>
                </div>
              </div>
              <p className="text-sm text-zinc-400" data-id="element-4916">
                3 active contacts with high response rates.
              </p>
            </div>
          </div>
        </div>}

      {activeTab === 'Contacts' && <div className="bg-[#14141A] border border-zinc-800 rounded-xl overflow-hidden" data-id="element-4917">
          <table className="w-full text-left text-sm" data-id="element-4918">
            <thead className="bg-zinc-900/50 border-b border-zinc-800 text-zinc-400" data-id="element-4919">
              <tr data-id="element-4920">
                <th className="px-4 py-3 font-medium" data-id="element-4921">Name</th>
                <th className="px-4 py-3 font-medium" data-id="element-4922">Role</th>
                <th className="px-4 py-3 font-medium" data-id="element-4923">Contact</th>
                <th className="px-4 py-3 font-medium" data-id="element-4924">Confidence</th>
                <th className="px-4 py-3 font-medium" data-id="element-4925">Last Verified</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-4926">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800" data-id="element-4927">
              {[{
            name: 'Brent Heyl',
            role: 'Talent Buyer',
            email: 'booking@emptybottle.com',
            conf: 'High',
            verified: '2 days ago'
          }, {
            name: 'Production Team',
            role: 'Production/Advancing',
            email: 'production@emptybottle.com',
            conf: 'High',
            verified: '1 week ago'
          }, {
            name: 'General Info',
            role: 'General',
            email: 'info@emptybottle.com',
            conf: 'Medium',
            verified: '1 month ago'
          }].map((contact, i) => <tr key={i} className="hover:bg-zinc-900/50 transition-colors" data-id="element-4928">
                  <td className="px-4 py-3 font-medium text-zinc-100" data-id="element-4929">
                    {contact.name}
                  </td>
                  <td className="px-4 py-3 text-zinc-400" data-id="element-4930">{contact.role}</td>
                  <td className="px-4 py-3" data-id="element-4931">
                    <div className="flex items-center space-x-2 text-zinc-300" data-id="element-4932">
                      <Mail className="w-3 h-3 text-zinc-500" data-id="element-4933" />
                      <span data-id="element-4934">{contact.email}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-4935">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${contact.conf === 'High' ? 'bg-green-500/10 text-green-500' : 'bg-amber-500/10 text-amber-500'}`} data-id="element-4936">
                      {contact.conf}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-500" data-id="element-4937">
                    {contact.verified}
                  </td>
                  <td className="px-4 py-3 text-right" data-id="element-4938">
                    <button className="p-1 text-zinc-500 hover:text-zinc-300 transition-colors" data-id="element-4939">
                      <MoreHorizontal className="w-4 h-4" data-id="element-4940" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}

      {activeTab === 'Fit' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4941">
          <div className="lg:col-span-2 space-y-6" data-id="element-4942">
            <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6" data-id="element-4943">
              <h3 className="text-lg font-medium text-zinc-100 mb-4" data-id="element-4944">
                Why This Venue Matches
              </h3>
              <p className="text-zinc-300 text-sm leading-relaxed mb-6" data-id="element-4945">
                The Empty Bottle is a strong match for your upcoming Midwest
                run. Their primary genres align perfectly with your sound, and
                their 400-cap room is the ideal step up from your last 250-cap
                sellout in Chicago.
              </p>
              <div className="grid grid-cols-2 gap-6" data-id="element-4946">
                <div data-id="element-4947">
                  <p className="text-sm text-zinc-500 mb-1" data-id="element-4948">Route Fit Score</p>
                  <div className="flex items-center space-x-3" data-id="element-4949">
                    <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden" data-id="element-4950">
                      <div className="h-full bg-green-500 rounded-full" style={{
                    width: '85%'
                  }} data-id="element-4951" />
                    </div>
                    <span className="text-green-500 font-bold" data-id="element-4952">85/100</span>
                  </div>
                </div>
                <div data-id="element-4953">
                  <p className="text-sm text-zinc-500 mb-1" data-id="element-4954">Price Alignment</p>
                  <div className="flex items-center space-x-2" data-id="element-4955">
                    <DollarSign className="w-4 h-4 text-green-500" data-id="element-4956" />
                    <span className="text-zinc-100 font-medium" data-id="element-4957">
                      Strong Match
                    </span>
                  </div>
                  <p className="text-xs text-zinc-400 mt-1" data-id="element-4958">
                    Your ask ($1.5k) fits their typical guarantee range.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6" data-id="element-4959">
              <h3 className="text-lg font-medium text-zinc-100 mb-4" data-id="element-4960">
                Comparable Artists
              </h3>
              <div className="space-y-3" data-id="element-4961">
                {[{
              name: 'The Midnight Echo',
              genre: 'Post-Punk',
              date: 'Played Mar 2026',
              draw: 'Sold Out (400)'
            }, {
              name: 'Crimson Tide',
              genre: 'Indie Rock',
              date: 'Played Jan 2026',
              draw: '320 Tickets'
            }, {
              name: 'Neon Dreams',
              genre: 'Indie Pop',
              date: 'Played Nov 2025',
              draw: '280 Tickets'
            }].map((artist, i) => <div key={i} className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg border border-zinc-800/50" data-id="element-4962">
                    <div data-id="element-4963">
                      <div className="font-medium text-zinc-100" data-id="element-4964">
                        {artist.name}
                      </div>
                      <div className="text-xs text-zinc-500" data-id="element-4965">
                        {artist.genre} • {artist.date}
                      </div>
                    </div>
                    <span className="text-sm font-medium text-zinc-300" data-id="element-4966">
                      {artist.draw}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>

          <div className="space-y-6" data-id="element-4967">
            <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6" data-id="element-4968">
              <h3 className="text-lg font-medium text-zinc-100 mb-4" data-id="element-4969">
                Risk Flags
              </h3>
              <div className="space-y-3" data-id="element-4970">
                <div className="flex items-start space-x-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg" data-id="element-4971">
                  <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" data-id="element-4972" />
                  <div data-id="element-4973">
                    <p className="text-sm font-medium text-amber-500" data-id="element-4974">
                      Radius Clause Strictness
                    </p>
                    <p className="text-xs text-amber-400/80 mt-1" data-id="element-4975">
                      Known to enforce strict 60-day / 60-mile radius clauses.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 p-3 bg-zinc-900/50 border border-zinc-800/50 rounded-lg" data-id="element-4976">
                  <CheckCircle className="w-5 h-5 text-zinc-500 shrink-0 mt-0.5" data-id="element-4977" />
                  <div data-id="element-4978">
                    <p className="text-sm font-medium text-zinc-300" data-id="element-4979">
                      Contact Verified
                    </p>
                    <p className="text-xs text-zinc-500 mt-1" data-id="element-4980">
                      Talent buyer email verified recently.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {activeTab === 'History' && <div className="space-y-6" data-id="element-4981">
          <div className="bg-[#14141A] border border-zinc-800 rounded-xl overflow-hidden" data-id="element-4982">
            <div className="p-4 border-b border-zinc-800 flex justify-between items-center bg-zinc-900/30" data-id="element-4983">
              <h3 className="font-medium text-zinc-100" data-id="element-4984">
                Past Outreach & Deals
              </h3>
            </div>
            <table className="w-full text-left text-sm" data-id="element-4985">
              <thead className="bg-zinc-900/50 border-b border-zinc-800 text-zinc-400" data-id="element-4986">
                <tr data-id="element-4987">
                  <th className="px-4 py-3 font-medium" data-id="element-4988">Date</th>
                  <th className="px-4 py-3 font-medium" data-id="element-4989">Campaign / Artist</th>
                  <th className="px-4 py-3 font-medium" data-id="element-4990">Type</th>
                  <th className="px-4 py-3 font-medium" data-id="element-4991">Status</th>
                  <th className="px-4 py-3 font-medium" data-id="element-4992">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800" data-id="element-4993">
                {[{
              date: 'Oct 12, 2025',
              campaign: 'Fall Run (Neon Dreams)',
              type: 'Deal',
              status: 'Confirmed',
              value: '$1,200'
            }, {
              date: 'May 04, 2025',
              campaign: 'Spring Tour (Crimson Tide)',
              type: 'Outreach',
              status: 'Passed',
              value: '--'
            }, {
              date: 'Jan 18, 2025',
              campaign: 'Winter One-Off (The Midnight Echo)',
              type: 'Deal',
              status: 'Confirmed',
              value: '$1,500'
            }].map((row, i) => <tr key={i} className="hover:bg-zinc-900/50 transition-colors" data-id="element-4994">
                    <td className="px-4 py-3 text-zinc-400" data-id="element-4995">{row.date}</td>
                    <td className="px-4 py-3 font-medium text-zinc-100" data-id="element-4996">
                      {row.campaign}
                    </td>
                    <td className="px-4 py-3 text-zinc-300" data-id="element-4997">{row.type}</td>
                    <td className="px-4 py-3" data-id="element-4998">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.status === 'Confirmed' ? 'bg-green-500/10 text-green-500' : 'bg-zinc-800 text-zinc-400'}`} data-id="element-4999">
                        {row.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-zinc-300" data-id="element-5000">{row.value}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6" data-id="element-5001">
            <h3 className="text-lg font-medium text-zinc-100 mb-4" data-id="element-5002">
              Internal Notes
            </h3>
            <div className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg" data-id="element-5003">
              <div className="flex items-center justify-between mb-2" data-id="element-5004">
                <span className="text-sm font-medium text-zinc-300" data-id="element-5005">
                  Alex M.
                </span>
                <span className="text-xs text-zinc-500" data-id="element-5006">Oct 15, 2025</span>
              </div>
              <p className="text-sm text-zinc-400" data-id="element-5007">
                Great room, sound guy was fantastic. Load-in is a bit tight
                through the front door, tell bands to arrive early. Brent
                (buyer) prefers email over calls.
              </p>
            </div>
          </div>
        </div>}

      {activeTab === 'Source Data' && <div className="space-y-6" data-id="element-5008">
          <div className="flex justify-between items-center" data-id="element-5009">
            <p className="text-sm text-zinc-400" data-id="element-5010">
              This venue's data is aggregated from multiple verified sources.
            </p>
            <button className="flex items-center space-x-2 px-3 py-1.5 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-300 text-sm font-medium rounded-lg transition-colors" data-id="element-5011">
              <RefreshCw className="w-4 h-4" data-id="element-5012" />
              <span data-id="element-5013">Refresh Data</span>
            </button>
          </div>

          <div className="bg-[#14141A] border border-zinc-800 rounded-xl overflow-hidden" data-id="element-5014">
            <table className="w-full text-left text-sm" data-id="element-5015">
              <thead className="bg-zinc-900/50 border-b border-zinc-800 text-zinc-400" data-id="element-5016">
                <tr data-id="element-5017">
                  <th className="px-4 py-3 font-medium" data-id="element-5018">Field</th>
                  <th className="px-4 py-3 font-medium" data-id="element-5019">Current Value</th>
                  <th className="px-4 py-3 font-medium" data-id="element-5020">Source</th>
                  <th className="px-4 py-3 font-medium" data-id="element-5021">Last Updated</th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-5022">Override</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800" data-id="element-5023">
                {[{
              field: 'Capacity (Main Room)',
              value: '400',
              source: 'Venue Website',
              updated: '2 days ago'
            }, {
              field: 'Talent Buyer Email',
              value: 'booking@emptybottle.com',
              source: 'Clearbit / Verified',
              updated: '1 week ago'
            }, {
              field: 'Age Policy',
              value: '21+',
              source: 'Venue Website',
              updated: '1 month ago'
            }, {
              field: 'Primary Genres',
              value: 'Indie Rock, Punk',
              source: 'Spotify API (Past Shows)',
              updated: '3 days ago'
            }].map((row, i) => <tr key={i} className="hover:bg-zinc-900/50 transition-colors" data-id="element-5024">
                    <td className="px-4 py-3 font-medium text-zinc-300" data-id="element-5025">
                      {row.field}
                    </td>
                    <td className="px-4 py-3 text-zinc-100" data-id="element-5026">{row.value}</td>
                    <td className="px-4 py-3" data-id="element-5027">
                      <div className="flex items-center space-x-1 text-zinc-400" data-id="element-5028">
                        <Database className="w-3 h-3" data-id="element-5029" />
                        <span data-id="element-5030">{row.source}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-500" data-id="element-5031">{row.updated}</td>
                    <td className="px-4 py-3 text-right" data-id="element-5032">
                      <button className="text-amber-500 hover:text-amber-400 text-xs font-medium" data-id="element-5033">
                        Edit
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
    </motion.div>;
}